import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import { Modal, Form, Alert, ProgressBar } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRefresh, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  faCalendarPlus,
  faCalendarCheck
} from '@fortawesome/free-regular-svg-icons';
import { UilCloudUpload } from '@iconscout/react-unicons';
import { ColumnDef, Row as TanstackRow } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown from 'components/base/RevealDropdown';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import HVASpinner from 'components/base/HVASpinner';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Dropdown } from 'react-bootstrap';
import {
  fetchCollections,
  createCollection,
  deleteCollection,
  KnowledgeHubCollection
} from '../../services/knowledgehub.service';
import HVAToast from 'components/common/HVAToast';
import DocumentUploader from './DocumentUploader';

interface CollectionsManagerProps {
  onUpdate?: () => void;
}

// Componente para las acciones de cada fila
const CollectionActionCell = ({
  collection,
  onDelete
}: {
  collection: KnowledgeHubCollection;
  onDelete: (id: string, name: string) => void;
}) => {
  return (
    <RevealDropdown
      btnClassName="p-2 pt-1 pb-1 fs-11"
      aria-label="Collection actions menu"
    >
      <Dropdown.Item onClick={() => onDelete(collection.id, collection.name)}>
        <FontAwesomeIcon
          icon={faTrash}
          className="fs-8 p-2 pt-0 pb-0 text-danger"
        />
        Delete Collection
      </Dropdown.Item>
    </RevealDropdown>
  );
};

const CollectionsManager: React.FC<CollectionsManagerProps> = ({
  onUpdate
}) => {
  const [collections, setCollections] = useState<KnowledgeHubCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [totalVectors, setTotalVectors] = useState(0);

  // Form state
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: ''
  });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    loadCollections();
  }, []);

  // Helper function to show toast
  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const loadCollections = async () => {
    try {
      setLoading(true);
      const data = await fetchCollections();
      setCollections(data);
      // Calcular el total de vectores de todas las colecciones
      const total = data.reduce(
        (sum, col) => sum + (col.vectorStoreInfo?.vectors_count || 0),
        0
      );
      setTotalVectors(total);
    } catch (err) {
      showToastMessage('Error loading collections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollection.name.trim()) {
      showToastMessage('Collection name is required', 'warning');
      return;
    }

    try {
      setCreating(true);
      await createCollection(newCollection.name, newCollection.description);
      setShowCreateModal(false);
      setNewCollection({ name: '', description: '' });
      await loadCollections();
      showToastMessage('Collection created successfully!', 'success');
      if (onUpdate) onUpdate();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.response?.status === 409) {
        showToastMessage('A collection with this name already exists', 'error');
      } else {
        showToastMessage('Error creating collection', 'error');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setCollectionToDelete({ id, name });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!collectionToDelete) return;

    try {
      setDeleting(true);
      await deleteCollection(collectionToDelete.id);
      setShowDeleteModal(false);
      setCollectionToDelete(null);
      await loadCollections();
      showToastMessage('Collection deleted successfully', 'success');

      if (onUpdate) onUpdate();
    } catch (err) {
      showToastMessage('Error deleting collection', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Definir columnas de la tabla
  const columns = useMemo<ColumnDef<KnowledgeHubCollection>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Collection Name',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => (
          <div className="d-flex align-items-center">
            <strong className="text-body-emphasis">{row.original.name}</strong>
          </div>
        ),
        meta: {
          headerProps: { style: { width: '15%' }, className: 'fs-10 px-1' },
          cellProps: { className: 'py-2 px-1' }
        }
      },
      {
        accessorKey: 'action',
        header: '',
        enableSorting: false,
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => (
          <CollectionActionCell
            collection={row.original}
            onDelete={handleDeleteClick}
          />
        ),
        meta: {
          headerProps: { style: { width: '2%' } },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => (
          <span className="text-body-tertiary">
            {row.original.description || '-'}
          </span>
        ),
        meta: {
          headerProps: { style: { width: '25%' }, className: 'fs-10' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'documentsCount',
        header: 'Documents',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => (
          <Badge bg="secondary" variant="phoenix">
            {row.original.documentsCount || 0}
          </Badge>
        ),
        meta: {
          headerProps: {
            style: { width: '10%' },
            className: 'fs-10 text-center'
          },
          cellProps: { className: 'py-2 text-center' }
        }
      },
      {
        accessorKey: 'vectorsCount',
        header: 'Vectors',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => (
          <Badge bg="secondary" variant="phoenix">
            {row.original.vectorStoreInfo?.vectors_count || 0}
          </Badge>
        ),
        meta: {
          headerProps: {
            style: { width: '10%' },
            className: 'fs-10 text-center'
          },
          cellProps: { className: 'py-2 text-center' }
        }
      },
      {
        header: 'Storage Usage',
        accessorKey: 'storageUsage', // Agregar accessorKey para ordenamiento
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => {
          const vectors = row.original.vectorStoreInfo?.vectors_count || 0;
          const usagePercent =
            totalVectors > 0 ? (vectors / totalVectors) * 100 : 0;

          // Determinar color según el porcentaje
          const variant =
            usagePercent > 50
              ? 'danger'
              : usagePercent > 30
              ? 'warning'
              : 'success';

          return (
            <div className="d-flex align-items-center">
              <ProgressBar
                now={usagePercent}
                style={{ width: '100px' }}
                className="me-2"
                variant={variant}
              />
              <small className="text-body-tertiary">
                {usagePercent.toFixed(1)}%
              </small>
            </div>
          );
        },
        sortingFn: (rowA, rowB) => {
          const vectorsA = rowA.original.vectorStoreInfo?.vectors_count || 0;
          const vectorsB = rowB.original.vectorStoreInfo?.vectors_count || 0;
          return vectorsA - vectorsB;
        },
        meta: {
          headerProps: {
            style: { width: '15%' },
            className: 'fs-10'
          },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => (
          <span className="text-body-tertiary">
            <FontAwesomeIcon icon={faCalendarPlus} className="me-1" />
            {formatDate(row.original.createdAt)}
          </span>
        ),
        meta: {
          headerProps: { style: { width: '12%' }, className: 'fs-10' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubCollection> }) => (
          <span className="text-body-tertiary">
            <FontAwesomeIcon icon={faCalendarCheck} className="me-1" />
            {formatDate(row.original.updatedAt)}
          </span>
        ),
        meta: {
          headerProps: { style: { width: '12%' }, className: 'fs-10' },
          cellProps: { className: 'py-2' }
        }
      }
    ],
    [totalVectors]
  );

  const table = useAdvanceTable({
    data: collections,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <>
      {/* Toast Notification */}
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Header */}
      <div className="mb-2 pt-3">
        <div className="d-flex flex-wrap gap-3">
          <SearchBox
            placeholder="Search collections..."
            onChange={handleSearchInputChange}
          />
          <div className="ms-auto">
            <div>
              <Button variant="link" onClick={loadCollections}>
                <FontAwesomeIcon icon={faRefresh} className="me-2" />
                Refresh
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowUploadModal(true)}
                className="me-2"
              >
                <UilCloudUpload size={18} className="me-2" />
                Upload Documents
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                New Collection
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Collections Table */}
      {loading ? (
        <HVASpinner />
      ) : collections.length === 0 ? (
        <Alert variant="subtle-secondary" className="fs-9">
          No collections yet. Create your first collection!
        </Alert>
      ) : (
        <AdvanceTableProvider {...table}>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-translucent position-relative top-1">
            <AdvanceTable
              tableProps={{
                size: 'sm',
                className: 'phoenix-table fs-9'
              }}
              rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
            />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>
      )}

      {/* Create Collection Modal */}
      <Modal
        show={showCreateModal}
        size="lg"
        onHide={() => setShowCreateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Collection</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-5">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Collection Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., policies, procedures, faqs"
                value={newCollection.name}
                onChange={e =>
                  setNewCollection({
                    ...newCollection,
                    name: e.target.value
                  })
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Optional description of this collection"
                value={newCollection.description}
                onChange={e =>
                  setNewCollection({
                    ...newCollection,
                    description: e.target.value
                  })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateModal(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateCollection}
            disabled={creating || !newCollection.name.trim()}
          >
            {creating ? 'Creating...' : 'Create Collection'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        size="lg"
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Collection</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6">
          <p className="mb-0">
            Are you sure you want to delete the collection{' '}
            <strong>{collectionToDelete?.name}</strong>?
          </p>
          <Alert variant="subtle-warning" className="mt-3 mb-0">
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            This action cannot be undone. All documents and vectors in this
            collection will be permanently deleted.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Collection'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Documents Modal */}
      <Modal
        show={showUploadModal}
        size="xl"
        onHide={() => setShowUploadModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Documents to Knowledge Hub</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 pt-4 pb-4">
          <DocumentUploader
            onUpdate={() => {
              loadCollections(); // Recargar documentos
              if (onUpdate) onUpdate(); // Actualizar stats si existe
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CollectionsManager;
